import { useEffect, useState } from "react";
import { api, apiErrorDetail, type SharingSettingsDoc } from "../../lib/api";

const DOMAIN_RE = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

/** Return whether an entry is a bare lowercase domain like "datacamp.com". */
export function isValidShareDomain(domain: string): boolean {
  return DOMAIN_RE.test(domain);
}

/** Render an ISO timestamp as a coarse "3h ago"; "—" when absent or unparseable. */
function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const seconds = (Date.now() - t) / 1000;
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/**
 * Admin → Sharing. Edit the email domains owners may share artifacts with
 * (spec §A10). Gates NEW grants only; never affects login or existing grants.
 */
export function SharingSettingsPage() {
  const [doc, setDoc] = useState<SharingSettingsDoc | null>(null);
  // null = no authoritative load yet — the form must NOT render from it.
  // A GET failure leaving an editable [] would let a later Save overwrite
  // the real policy with the kill switch.
  const [draft, setDraft] = useState<string[] | null>(null);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.admin.sharingSettings
      .get()
      .then((d) => {
        setDoc(d);
        setDraft(d.allowed_share_domains);
      })
      .catch((e: unknown) => {
        if (e instanceof Error && e.message === "redirecting-to-login") return;
        setError(apiErrorDetail(e));
      });
  }, []);

  /** Validate the typed domain and move it into the draft list. */
  function addDomain() {
    if (draft === null) return;
    const domain = input.trim().toLowerCase();
    if (!isValidShareDomain(domain)) {
      setError(`"${input}" is not a bare domain like partner.io`);
      return;
    }
    setError(null);
    if (!draft.includes(domain)) setDraft([...draft, domain]);
    setInput("");
  }

  /** Drop one domain from the draft list (persisted on Save, not immediately). */
  function removeDomain(domain: string) {
    setDraft((cur) => (cur ? cur.filter((d) => d !== domain) : cur));
  }

  /** PUT the draft list; the server normalises, validates, and audits. */
  async function save() {
    if (doc === null || draft === null) return; // never PUT without a loaded doc
    setSaving(true);
    setError(null);
    try {
      const updated = await api.admin.sharingSettings.put(draft);
      setDoc(updated);
      setDraft(updated.allowed_share_domains);
    } catch (e: unknown) {
      setError(apiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  }

  // Failed load → error + retry only; NEVER an editable empty form.
  if (doc === null || draft === null) {
    return error ? (
      <p className="text-sm text-rose-700">{error} — reload to retry.</p>
    ) : (
      <p className="text-sm text-slate-500">Loading…</p>
    );
  }

  const dirty =
    JSON.stringify(draft) !== JSON.stringify(doc.allowed_share_domains);

  return (
    <div>
      <h1 className="mb-2 text-2xl font-semibold">Sharing domains</h1>
      <p className="mb-4 text-sm text-slate-500">
        Owners can share artifacts with people at these domains. Adding a
        domain does NOT let its users sign in — only @datacamp.com can log in
        until a guest-access project ships. Removing a domain stops new shares
        but does not revoke existing ones. An empty list disables new sharing
        entirely.
      </p>

      <div className="space-y-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="text-sm font-semibold">Allowed domains</h2>
            <span className="text-xs text-slate-500">
              {draft.length} domain{draft.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="mb-2 flex flex-wrap gap-2">
            {draft.length === 0 && (
              <span className="text-xs italic text-slate-400">
                No domains allowed — new sharing is disabled.
              </span>
            )}
            {draft.map((domain) => (
              <span
                key={domain}
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs"
              >
                <code className="font-mono">{domain}</code>
                <button
                  type="button"
                  aria-label={`Remove ${domain}`}
                  onClick={() => removeDomain(domain)}
                  className="rounded-full px-1 text-slate-500 hover:bg-slate-200 hover:text-slate-900"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="partner.io"
              aria-label="Add sharing domain"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addDomain();
                }
              }}
              className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 font-mono text-sm"
            />
            <button
              type="button"
              onClick={addDomain}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Add
            </button>
          </div>
          {error && (
            <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {error}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty}
            className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <p className="mt-4 text-xs text-slate-400">
        Updated {relativeTime(doc.updated_at)}
        {doc.updated_by ? ` by ${doc.updated_by}` : ""}.
      </p>
    </div>
  );
}
